import type { TourItem, TourSearchContextForSave, TourToIdeaPayload } from '../types/tour.types';
import { coerceDiscoverString, normalizeDiscoverPayload } from './discoverPayload.utils';
import { formatTourDurationDisplay } from './tourSearch.utils';

export function newTourExternalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tour-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function parseDurationDaysFromString(duration: TourItem['duration']): number | undefined {
  const raw = formatTourDurationDisplay(duration);
  if (!raw) return undefined;
  const match = raw.match(/(\d+)/);
  if (!match) return undefined;
  const n = Number.parseInt(match[1], 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function resolveDurationDays(tour: TourItem, searchContext: TourSearchContextForSave): number | undefined {
  const fromDuration = parseDurationDaysFromString(tour.duration);
  if (fromDuration != null) return fromDuration;

  if (searchContext.durationDays != null && searchContext.durationDays >= 0) {
    return searchContext.durationDays;
  }

  return undefined;
}

export function buildTourToIdeaPayload(
  tour: TourItem,
  searchContext: TourSearchContextForSave
): TourToIdeaPayload {
  const external_id = coerceDiscoverString(tour.external_id) || newTourExternalId();
  const discover_payload =
    normalizeDiscoverPayload(tour.discover_payload) ||
    JSON.stringify({
      v: 1,
      source: 'discover_tour_search',
      from_location: searchContext.fromLocation,
      to_location: searchContext.toLocation,
      depart_date: searchContext.departDate ?? coerceDiscoverString(tour.depart_date) ?? null,
      duration_days: searchContext.durationDays ?? null,
      product_code: coerceDiscoverString(tour.product_code) ?? null,
      provider: coerceDiscoverString(tour.provider) ?? null,
      supplier_name: coerceDiscoverString(tour.supplier_name) ?? null,
      price: coerceDiscoverString(tour.price) ?? null,
      currency: coerceDiscoverString(tour.currency) ?? null,
    });

  const duration_days = resolveDurationDays(tour, searchContext);

  const payload: TourToIdeaPayload = {
    external_id,
    discover_payload,
    product_code: coerceDiscoverString(tour.product_code),
    name: coerceDiscoverString(tour.name) || 'Tour',
    description: coerceDiscoverString(tour.description),
    image_url: coerceDiscoverString(tour.image_url),
    link: coerceDiscoverString(tour.link),
    price: coerceDiscoverString(tour.price),
    currency: coerceDiscoverString(tour.currency),
    duration: formatTourDurationDisplay(tour.duration) || undefined,
    from_location: coerceDiscoverString(tour.from_location) || searchContext.fromLocation || undefined,
    to_location: coerceDiscoverString(tour.to_location) || searchContext.toLocation || undefined,
    depart_date:
      coerceDiscoverString(tour.depart_date)?.slice(0, 10) ||
      searchContext.departDate?.trim().slice(0, 10) ||
      undefined,
  };

  if (duration_days != null) {
    payload.duration_days = duration_days;
  }

  return payload;
}
